import { Link, useLocation } from 'wouter';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  X,
  LayoutDashboard,
  Calendar,
  Bell,
  UsersRound,
  Building2,
  QrCode,
  Users,
  Database,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { useAuth, ALL_FEATURES_ENABLED, type FeatureKey, type SiteFeatures } from '@/auth/AuthContext';
import { useSelectedSite } from '@/auth/useSelectedSite';
import { useViewMode } from '@/auth/useViewMode';
import { cn } from '@/lib/utils';
import { SiteSwitcher } from './SiteSwitcher';
import { ViewModeToggle } from './AppShell';

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  feature?: FeatureKey;
}

export function MobileMoreSheet({
  open,
  onClose,
  currentSlug,
}: {
  open: boolean;
  onClose: () => void;
  currentSlug: string;
}) {
  const [, navigate] = useLocation();
  const { isGlobalAdmin } = useAuth();
  const { accessibleSites, selectedSlug, selectSlug } = useSelectedSite();
  const { viewMode, toggleViewMode, canSwitchView } = useViewMode();

  const currentSite = accessibleSites.find((s) => s.slug === currentSlug);
  const currentFeatures = currentSite?.features ?? ALL_FEATURES_ENABLED;
  const showAdminSection = isGlobalAdmin && (!canSwitchView || viewMode === 'admin');

  const close = () => onClose();
  const go = (href: string) => {
    navigate(href);
    close();
  };
  const onPickSite = (slug: string) => {
    selectSlug(slug);
    close();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-xl bg-background border-t border-border shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
            'pb-[env(safe-area-inset-bottom)]',
          )}
        >
          <DialogPrimitive.Title className="sr-only">More navigation</DialogPrimitive.Title>
          <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-border bg-background">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {currentSite?.name ?? 'Current site'}
              </div>
              <div className="text-sm font-bold">More</div>
            </div>
            <button
              onClick={close}
              className="h-9 w-9 rounded-md flex items-center justify-center hover-elevate"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-3 space-y-5">
            <div className="space-y-2">
              <SiteSwitcher
                sites={accessibleSites}
                selectedSlug={selectedSlug}
                onSelect={onPickSite}
                variant="sheet"
              />
              {canSwitchView && (
                <ViewModeToggle viewMode={viewMode} onToggle={toggleViewMode} variant="sheet" />
              )}
            </div>

            <Section
              label="Home"
              items={[{ label: 'Dashboard', href: '/', icon: LayoutDashboard }]}
              onSelect={go}
            />

            {currentSlug && (
              <FilteredSection
                label={currentSite?.name ?? 'Current site'}
                features={currentFeatures}
                items={[
                  { label: 'Tours', href: `/sites/${currentSlug}/tours`, icon: Calendar, feature: 'tours' },
                  { label: 'Census', href: `/sites/${currentSlug}/census`, icon: UsersRound, feature: 'census' },
                  { label: 'Alerts', href: `/sites/${currentSlug}/alerts`, icon: Bell, feature: 'alerts' },
                ]}
                onSelect={go}
              />
            )}

            {showAdminSection && (
              <Section
                label="Administration"
                items={[
                  { label: 'Sites', href: '/admin/sites', icon: Building2 },
                  { label: 'Checkpoints', href: '/admin/checkpoints', icon: QrCode },
                  { label: 'Users', href: '/admin/users', icon: Users },
                  { label: 'Census', href: '/admin/census', icon: Database },
                  { label: 'Settings', href: '/admin/settings', icon: Settings },
                ]}
                onSelect={go}
              />
            )}

            <Section
              label="Account"
              items={[{ label: 'My Preferences', href: '/me/preferences', icon: Settings }]}
              onSelect={go}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FilteredSection({
  label,
  items,
  features,
  onSelect,
}: {
  label: string;
  items: NavItem[];
  features: SiteFeatures;
  onSelect: (href: string) => void;
}) {
  const visible = items.filter((i) => !i.feature || features[i.feature]);
  if (visible.length === 0) return null;
  return <Section label={label} items={visible} onSelect={onSelect} />;
}

function Section({
  label,
  items,
  onSelect,
}: {
  label: string;
  items: NavItem[];
  onSelect: (href: string) => void;
}) {
  return (
    <div>
      <div className="px-1 mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <ul className="space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.href}>
              <Link
                href={item.href}
                onClick={() => onSelect(item.href)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover-elevate"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
